"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import AIBadge from "@/components/ui/ai-badge";
import Button from "@/components/ui/button";
import { cx } from "@/lib/tailwind-utils";
import type { LabResultsSummaryContext, LabResultsSummaryResponse } from "@/types/ai";
import type { LabBiomarkerResult, LabOrder } from "@/types/lab";
import { streamLabResultsSummary } from "@/api/ai/lab";
interface LabResultsAISummaryProps {
  results: LabBiomarkerResult[];
  order: LabOrder;
}
function buildContext(results: LabBiomarkerResult[], order: LabOrder): LabResultsSummaryContext {
  return {
    results: results.map(r => ({
      markerName: r.markerName,
      valueDisplay: r.valueDisplay,
      unit: r.unit,
      referenceRangeText: r.referenceRangeText ?? (r.minRangeValue != null && r.maxRangeValue != null ? `${r.minRangeValue} - ${r.maxRangeValue}` : undefined),
      clinicalInterpretation: r.clinicalInterpretation,
      notes: r.notes
    })),
    labName: order.labName ?? order.labSlug,
    icdCodes: order.icdCodes,
    clinicalNotes: order.clinicalNotes,
    fasting: order.fasting,
    sampleType: order.sampleType,
    physicianName: [order.physicianFirstName, order.physicianLastName].filter(Boolean).join(" ") || undefined
  };
}

/**
 * Extract the summary text from partial/complete streamed JSON.
 */
function extractSummaryFromPartialJSON(text: string): string {
  if (!text) return text;
  try {
    const parsed = JSON.parse(text.trim());
    if (parsed && typeof parsed === "object" && "summary" in parsed) {
      return String(parsed.summary || "").trim();
    }
  } catch {
    // Not complete JSON yet
  }
  const summaryRegex = /"summary"\s*:\s*"((?:[^"\\]|\\.)*)"/;
  const summaryMatch = text.match(summaryRegex);
  if (summaryMatch?.[1]) {
    return summaryMatch[1].replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }
  const fallbackMatch = text.match(/"summary"\s*:\s*"([^"]*)/);
  if (fallbackMatch?.[1]) {
    return fallbackMatch[1].replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }
  return text.replace(/^\s*\{\s*"summary"\s*:\s*"/, "").replace(/"\s*,\s*"highlights"\s*:.*$/, "").replace(/"\s*\}$/, "").replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\").replace(/^[\s{"]+/, "").replace(/["}\s,]+$/, "");
}
export default function LabResultsAISummary({
  results,
  order
}: LabResultsAISummaryProps) {
  const [streamingText, setStreamingText] = useState("");
  const [parsedResponse, setParsedResponse] = useState<LabResultsSummaryResponse | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasGeneratedRef = useRef(false);
  const generate = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsStreaming(true);
    setStreamingText("");
    setParsedResponse(null);
    setError(null);
    let fullText = "";
    try {
      const context = buildContext(results, order);
      for await (const chunk of streamLabResultsSummary(context, controller.signal)) {
        fullText += chunk;
        setStreamingText(fullText);
      }

      // Try to parse the complete response
      try {
        const parsed = JSON.parse(fullText.trim());
        setParsedResponse(parsed);
      } catch {
        // If JSON parsing fails, show the extracted summary text
        setParsedResponse({
          summary: extractSummaryFromPartialJSON(fullText),
          highlights: []
        });
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : "Failed to generate summary");
    } finally {
      setIsStreaming(false);
    }
  }, [results, order]);

  // Auto-generate on mount
  useEffect(() => {
    if (hasGeneratedRef.current) return;
    hasGeneratedRef.current = true;
    generate();
    return () => abortControllerRef.current?.abort();
  }, [generate]);

  // Display text: parsed response takes priority, then streaming extraction
  const displaySummary = parsedResponse ? parsedResponse.summary : extractSummaryFromPartialJSON(streamingText);
  const highlights = parsedResponse?.highlights ?? [];
  if (error) {
    return <div className="rounded-lg border border-border bg-background-secondary p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AIBadge size={16} />
            <span className="text-body-strong">AI Results Summary</span>
          </div>
          <Button variant="ghost" size="sm" onClick={generate}>
            <FiRefreshCw className="mr-1.5 h-3 w-3" />
            Retry
          </Button>
        </div>
        <p className="mt-2 text-body text-status-error-text">{error}</p>
      </div>;
  }
  if (!displaySummary && !isStreaming) return null;
  return <div className="rounded-lg border border-border bg-background-secondary p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AIBadge size={16} />
          <span className="text-body-strong">AI Results Summary</span>
        </div>
        <Button variant="ghost" size="sm" onClick={generate} disabled={isStreaming}>
          <FiRefreshCw className={cx("mr-1.5 h-3 w-3", isStreaming && "animate-spin")} />
          Regenerate
        </Button>
      </div>

      {/* Summary text */}
      <p className="mt-2 text-body text-foreground-secondary">
        {displaySummary}
        {isStreaming && !parsedResponse && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-foreground-secondary" />}
      </p>

      {/* Highlights */}
      {highlights.length > 0 && <ul className="mt-3 space-y-1">
          {highlights.map((highlight, i) => <li key={i} className="flex items-start gap-2 text-body text-foreground-secondary">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground-muted" />
              {highlight}
            </li>)}
        </ul>}
    </div>;
}